import React, { useEffect } from 'react';
import {Outlet, useLocation} from 'react-router-dom';
import HeaderBar from '../elements/HeaderBar';
import MessageModal from '../modals/MessageModal';
import { Server } from '../../server/server';
import { AppConfig } from '../AppConfig';

const INACTIVITY_TIME = 5*60;
const SHUTDOWN_TIME = 4*60*60;
const APP_VERSION = 11;

const ScrollToTop: React.FC = () => {
  const location = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);
  return null;
};

interface SitePageState {
  message: string;
  shutdown: boolean;
  version: boolean;
  maintenance: boolean;
  bypass: boolean;
  navigate: string;
}

class SitePage extends React.Component<{}, SitePageState> {
  public static isActive: boolean = true;
  public static isShutdown: boolean = false;
  public static awayElapsed: number = 0;
  public static mouseElapsed: number = 0;
  public static notice: any = null;

  protected static listeners:any[] = [];
  
  protected disconnectedCount:number;
  protected awayStart: number;
  protected lastMouseTime: number;
  protected inactivityInterval:any;
  protected statusInterval:any;

  constructor(props: {}) {
    super(props);
    this.state = {
      message: '',
      shutdown: false,
      version: false,
      maintenance: false,
      bypass: false,
      navigate: ''
    }

    this.disconnectedCount = 0;
    this.inactivityInterval = null;
    this.statusInterval = null;

    this.onRecordLastMouseTime = this.onRecordLastMouseTime.bind(this);
    this.onVisibilityChange = this.onVisibilityChange.bind(this);
  }

  public static addEventListener(callback:Function) {
    SitePage.listeners.push(callback);
  }

  public static removeEventListener(callback:Function) {
    for(let i = 0; i < SitePage.listeners.length; i++) {
      if(SitePage.listeners[i] == callback) {
        SitePage.listeners.splice(i, 1);
        return;
      }
    }
  }

  public static notifyListeners(event:string) {
    for(let i = 0; i < SitePage.listeners.length; i++) 
      SitePage.listeners[i](event);
  }

  componentDidMount() {
    SitePage.isActive = true;
    SitePage.isShutdown = false;
    this.lastMouseTime = performance.now();

    window.addEventListener('mousemove', this.onRecordLastMouseTime);
    window.addEventListener('mousedown', this.onRecordLastMouseTime);
    window.addEventListener('mouseup', this.onRecordLastMouseTime);
    window.addEventListener('wheel', this.onRecordLastMouseTime);
    window.addEventListener('focus', this.onRecordLastMouseTime);
    window.addEventListener('touchstart', this.onRecordLastMouseTime);
    window.addEventListener('touchmove', this.onRecordLastMouseTime);
    window.addEventListener('touchend', this.onRecordLastMouseTime);
    document.addEventListener('visibilitychange', this.onVisibilityChange);
    window.addEventListener('keydown', this.onRecordLastMouseTime);
    Server.addEventListener('login', this.onRecordLastMouseTime);
    Server.addEventListener('logout', this.onRecordLastMouseTime);

    this.inactivityInterval = setInterval(() => {
      this.checkForInactivity();
    }, 1000);

    this.statusInterval = setTimeout(() => {
      this.checkStatus();
    }, 5 * 1000);
  }

  componentWillUnmount() {
    window.removeEventListener('mousemove', this.onRecordLastMouseTime);
    window.removeEventListener('mousedown', this.onRecordLastMouseTime);
    window.removeEventListener('mouseup', this.onRecordLastMouseTime);
    window.removeEventListener('wheel', this.onRecordLastMouseTime);
    window.removeEventListener('focus', this.onRecordLastMouseTime);
    window.removeEventListener('touchstart', this.onRecordLastMouseTime);
    window.removeEventListener('touchmove', this.onRecordLastMouseTime);
    window.removeEventListener('touchend', this.onRecordLastMouseTime);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    window.removeEventListener('keydown', this.onRecordLastMouseTime);
    Server.removeEventListener('login', this.onRecordLastMouseTime);
    Server.removeEventListener('logout', this.onRecordLastMouseTime);
    clearInterval(this.inactivityInterval);
    this.inactivityInterval = null;
    clearInterval(this.statusInterval);
    this.statusInterval = null;
  }

  onVisibilityChange() {
    if(document.visibilityState === 'visible')
      this.lastMouseTime = performance.now();
  }

  onRecordLastMouseTime() {
    this.lastMouseTime = performance.now();
  }

  async returnFromBeingAway() {
    if(!AppConfig.community)
      return;
    let promises = [];
    promises.push(Server.network.connect());
    if(!Server.monitor.isCheckingForNewContent())
      promises.push(Server.monitor.checkForNewChats(true, 0));
    promises.push(Server.user.sync());
    await Promise.all(promises);

    SitePage.notifyListeners('return-from-away')
  }

  checkForInactivity() {
    if(!Server.isLoggedIn() || SitePage.isShutdown)
      return;

    SitePage.mouseElapsed = (performance.now() - this.lastMouseTime) / 1000;
    let active = document.hasFocus() && SitePage.mouseElapsed < 30;

    if(active && !SitePage.isActive) {
      if(!Server.network.isConnected()) 
        this.returnFromBeingAway();
    }
    else if(!active && SitePage.isActive) {
      this.awayStart = performance.now();
    }

    SitePage.isActive = active;

    if(SitePage.isActive) {
      SitePage.awayElapsed = 0;
    }
    else {
      SitePage.awayElapsed = (performance.now() - this.awayStart) / 1000;

      if(SitePage.awayElapsed > INACTIVITY_TIME) { 
        if(Server.network.isConnected()) {
          Server.network.disconnect();
        }
      } 

      if(SitePage.awayElapsed > SHUTDOWN_TIME && !SitePage.isShutdown) {
        SitePage.isShutdown = true;
        this.setState({shutdown: true})
      } 
    }
  }

  async checkStatus() {
    if(SitePage.isShutdown)
      return;

    let isProduction = !window.location.hostname.includes('localhost');
    if(!isProduction)
      console.log('checkStatus', new Date().toLocaleTimeString());
    
    let response:any = await Server.public.getStatus();

    if(!response.success)
      return;

    let newNotice = null;

    if(response.status.maintenance && !this.state.bypass) {
      Server.network.disconnect();
      this.setState({maintenance: true});
      SitePage.isShutdown = true;
    }
    else if(APP_VERSION < response.status.version) {
      Server.network.disconnect();
      this.setState({version: true});
      SitePage.isShutdown = true;
    }
    else if(response.status.notice && response.status.notice != '') {
      newNotice = new Date(response.status.notice);
      setTimeout(() => {
        this.checkStatus();
      }, 60 * 1000);
    }
    else {
      setTimeout(() => {
        this.checkStatus();
      }, 5 * 60 * 1000);
    }

    if(SitePage.notice != newNotice) {
      SitePage.notice = newNotice;
      this.forceUpdate();
    }
  }

  onLogin() {
    // this.setState({login: true});
    Server.login();
  }

  onLogout() {
    this.setState({message: 'Logging out...'});

    setTimeout(async () => {
      await Server.logout();
      // window.location.assign(`https://api.oneclick.community/v3/auth/logout?next=${encodeURIComponent(window.location.origin)}`);
    }, 1000);
  }

  onRefresh() {
    window.location.href = "/";
  }

  renderModal(message:string) {
    return (
      <div className="modal open">
        <div className="modal-content" style={{width: '300px', textAlign: 'center', rowGap: '15px'}}>
          {message}
          <div><button onClick={()=>this.onRefresh()}>Refresh</button></div>
        </div>
      </div>
    )
  }

  render() {
    let modal = null;
    if(this.state.maintenance && !this.state.bypass)
      modal = this.renderModal('The site is down for maintenance.  Please refresh the browser.');
    else if(this.state.version)
      modal = this.renderModal('There is a new version available!  Please refresh the browser to get the latest updates.');
    else if(this.state.shutdown)    
      modal = this.renderModal('You have been disconnected due to inactivity.  Please refresh the browser to reconnect.');

    let noticeDiv = null;
    if(SitePage.notice) {
      let now = new Date();
      let remaining = (SitePage.notice.getTime() - now.getTime()) / 1000;
      let minutes = Math.floor(remaining / 60);
      let message = '';
      if(minutes <= 0)
        message = 'Maintenence will begin any moment.'
      else 
        message = `Maintenance will start in ${minutes} minute${minutes > 1 ? 's' : ''}.`;
      noticeDiv = <div style={{backgroundColor: 'yellow', color: 'black', textAlign: 'center', padding: '10px'}}>{message}</div>
    }
    else if(AppConfig.community && AppConfig.community.restrictions && (!AppConfig.community.restrictions.newUsers || AppConfig.community.restrictions.readOnly) && AppConfig.community.host == Server.user.getId()) {
      noticeDiv = <div style={{backgroundColor: 'yellow', color: 'black', textAlign: 'center', padding: '10px'}}>This community is currently restricted.  See Admin page for details.</div>
    }
    else if(AppConfig.community && AppConfig.community.restrictions && AppConfig.community.restrictions.readOnly) {
      noticeDiv = <div style={{backgroundColor: 'yellow', color: 'black', textAlign: 'center', padding: '10px'}}>This community is read only.</div>
    }

    return (
      <div id="id-app-page" style={{minHeight: '100dvh', display: 'flex', flexDirection: 'column'}}>
        <ScrollToTop />
        <HeaderBar onLogin={()=>this.onLogin()} onLogout={()=>this.onLogout()}/>
        {noticeDiv && noticeDiv}
        <Outlet />
        <MessageModal message={this.state.message} />
        {modal}
      </div>
    )
  }
}

export default SitePage;
